import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer({
  isMobile = false,
  onLinkClick
}: {
  isMobile?: boolean;
  onLinkClick?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="13884" className={`flex ${isMobile ? "flex-col gap-4" : "gap-6"}`}>
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-5 w-20 bg-white/5 animate-pulse rounded-none" />)}
      </div>;
  }
  if (isMobile) {
    return <nav className="flex flex-col gap-6 text-center">
        {menuItems.map((item, i) => <div key={item.id} data-index={i}>
            <Link to={item.href || "#"} className="text-xl font-sans font-normal uppercase tracking-widest text-white hover:text-secondary transition-colors" onClick={onLinkClick}>
              {item.label}
            </Link>
          </div>)}
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-xs font-sans font-normal uppercase tracking-widest text-white transition-colors hover:text-secondary">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-[1px] w-0 bg-secondary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="navigazione-strategica" className={`sticky top-0 z-50 w-full transition-all duration-300 border-b ${isScrolled ? "bg-[#0A0A0A]/70 backdrop-blur-[20px] border-secondary/20 shadow-none" : "bg-[#0A0A0A] border-transparent"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="block">
            <span className="text-2xl font-serif font-bold text-white tracking-tight">
              geroi.eu
            </span>
          </Link>
        </div>

        {}
        <div className="hidden md:flex items-center gap-8">
          <MenuProvider menu_id="16" className="flex items-center">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex items-center">
          <Button variant="outline" className="bg-[#DC143C] border-none text-white hover:bg-white hover:text-[#0A0A0A] transition-all duration-300 rounded-none px-6 font-serif uppercase tracking-widest text-xs h-10" asChild>
            <Link to="#">Audit di Salvataggio</Link>
          </Button>
        </div>

        {}
        <div className="md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white hover:text-secondary hover:bg-transparent" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="md:hidden fixed inset-0 top-20 z-40 bg-[#0A0A0A] border-t border-white/10 flex flex-col p-6 animate-in slide-in-from-top-5 fade-in duration-200">
          <div className="flex-1 flex flex-col items-center justify-center gap-8">
            <MenuProvider menu_id="16">
              <HeaderMenuConsumer isMobile onLinkClick={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>

            <div className="mt-8 w-full max-w-xs">
              <Button variant="outline" className="w-full bg-[#DC143C] border-none text-white hover:bg-white hover:text-[#0A0A0A] h-14 text-lg font-serif uppercase tracking-widest rounded-none" asChild onClick={() => setIsMobileMenuOpen(false)}>
                <Link to="#">Audit di Salvataggio</Link>
              </Button>
            </div>
          </div>
        </div>}
    </header>;
}